import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const KEY = "sw_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch (err) {
      console.warn("CartContext: failed to parse stored cart", err);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (err) {
      console.warn("CartContext: failed to persist cart", err);
    }
  }, [items]);

  const remove = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const add = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        image_url: product.image_url,
        price: product.price,
        seller_id: product.seller_id,
        seller_name: product.seller_name,
        quantity: qty,
      }];
    });
  }, []);

  const setQty = useCallback((product_id, quantity) => {
    if (quantity <= 0) {
      remove(product_id);
      return;
    }
    setItems((prev) => prev.map((i) => i.product_id === product_id ? { ...i, quantity } : i));
  }, [remove]);

  const clear = useCallback(() => setItems([]), []);

  const total = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, add, setQty, remove, clear, total, count }),
    [items, add, setQty, remove, clear, total, count]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
